import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { getSession, onAuthStateChange, updatePassword } from '@/lib/queries/auth'
import PasswordField from '@/components/auth/PasswordField'
import {
  MIN_PASSWORD_LENGTH,
  RESET_LINK_VERIFY_DELAY_MS,
  POST_RESET_REDIRECT_DELAY_MS,
} from '@/lib/constants'

export default function ResetPassword() {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  // A11Y-009: per-field errors so screen readers announce the right
  // failing field via aria-describedby. formError covers reset-link
  // verification failures and server errors that aren't tied to a
  // single password input.
  const [passwordError, setPasswordError] = useState(null)
  const [confirmPasswordError, setConfirmPasswordError] = useState(null)
  const [formError, setFormError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [ready, setReady] = useState(false)
  const [linkChecked, setLinkChecked] = useState(false)
  const [done, setDone] = useState(false)

  function clearAllErrors() {
    setPasswordError(null)
    setConfirmPasswordError(null)
    setFormError(null)
  }
  const navigate = useNavigate()
  const redirectTimerRef = useRef(null)

  // Clear the post-success redirect timer on unmount so it can't fire after
  // the user has navigated elsewhere.
  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    let invalidTimer = null

    function markReady() {
      if (cancelled) return
      if (invalidTimer) clearTimeout(invalidTimer)
      setReady(true)
      setLinkChecked(true)
      clearAllErrors()
    }

    const subscription = onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session?.user)) {
        markReady()
      }
    })

    ;(async () => {
      try {
        const session = await getSession()
        if (session?.user) {
          markReady()
          return
        }

        invalidTimer = setTimeout(() => {
          if (cancelled) return
          setFormError('This reset link is invalid or has expired. Please request a new one.')
          setLinkChecked(true)
        }, RESET_LINK_VERIFY_DELAY_MS)
      } catch {
        if (cancelled) return
        setFormError('We could not verify your reset link. Please request a new one.')
        setLinkChecked(true)
      }
    })()

    return () => {
      cancelled = true
      if (invalidTimer) clearTimeout(invalidTimer)
      subscription.unsubscribe()
    }
  }, [])

  async function handleSubmit(e) {
    e.preventDefault()
    clearAllErrors()
    let hasError = false
    if (password.length < MIN_PASSWORD_LENGTH) {
      setPasswordError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`)
      hasError = true
    }
    if (password && password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match.')
      hasError = true
    }
    if (hasError) return
    setSubmitting(true)
    try {
      await updatePassword(password)
    } catch (err) {
      setFormError(err.message)
      setSubmitting(false)
      return
    }
    setDone(true)
    redirectTimerRef.current = setTimeout(() => navigate('/login'), POST_RESET_REDIRECT_DELAY_MS)
  }

  if (done) {
    return (
      <div className="max-w-md w-full">
        <div className="bg-[var(--bg-surface)] border border-[var(--glass-border)] rounded-2xl p-8 md:p-10 text-center">
          <h2 className="font-heading text-[var(--text-primary)] text-2xl font-bold mb-4">
            Password Updated
          </h2>
          <p className="text-[var(--text-muted)] text-sm">
            Your password has been reset successfully. Redirecting you to login...
          </p>
        </div>
      </div>
    )
  }

  if (!linkChecked) {
    return (
      <div className="max-w-md w-full">
        <div className="bg-[var(--bg-surface)] border border-[var(--glass-border)] rounded-2xl p-8 md:p-10 text-center">
          <p className="text-[var(--text-muted)] text-sm">Verifying your reset link...</p>
        </div>
      </div>
    )
  }

  if (!ready) {
    return (
      <div className="max-w-md w-full">
        <div className="bg-[var(--bg-surface)] border border-[var(--glass-border)] rounded-2xl p-8 md:p-10 text-center">
          <h2 className="font-heading text-[var(--text-primary)] text-2xl font-bold mb-4">
            Reset Link Expired
          </h2>
          <p className="text-[var(--text-muted)] text-sm mb-6">
            {formError ||
              'This reset link is no longer valid. Please request a new password reset email.'}
          </p>
          <button
            type="button"
            onClick={() => navigate('/login', { replace: true })}
            className="text-gold text-sm hover:underline"
          >
            Back to Login
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-md w-full">
      <div className="bg-[var(--bg-surface)] border border-[var(--glass-border)] rounded-2xl p-8 md:p-10">
        <h2 className="font-heading text-[var(--text-primary)] text-2xl font-bold mb-2 text-center">
          Set New Password
        </h2>
        <p className="text-[var(--text-muted)] text-sm text-center mb-8">
          Choose a strong password for your account.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <PasswordField
            label="New password"
            placeholder="New password"
            autoComplete="new-password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            error={passwordError}
            className="w-full px-4 py-3 pr-12 rounded-lg bg-[var(--bg-surface-high)] border border-[var(--glass-border)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-gold focus:border-gold transition-all"
          />

          <PasswordField
            label="Confirm new password"
            placeholder="Confirm new password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            error={confirmPasswordError}
            className="w-full px-4 py-3 pr-12 rounded-lg bg-[var(--bg-surface-high)] border border-[var(--glass-border)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-gold focus:border-gold transition-all"
          />

          {formError && (
            <p role="alert" className="text-[var(--feedback-negative)] text-sm text-center">
              {formError}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-gold hover:bg-gold-light text-midnight font-bold py-3.5 rounded-lg text-lg transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100"
          >
            {submitting ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  )
}
